import { CheckCircle2, ChevronDown, LoaderCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { checkDeviceEndpoint } from '../../api/devices';
import { ConnectionTest, PasswordField, type ConnectionState } from './FormControls';
import type { DeviceFormProps } from './deviceTypes';

interface RecorderFormState {
  name: string;
  ipAddress: string;
  username: string;
  password: string;
  port: string;
  httpPort: string;
  rtspPort: string;
  onvifPort: string;
  channelCount: string;
  vendor: string;
  model: string;
  recorderType: 'dvr' | 'nvr' | 'xvr';
  protocol: 'rtsp' | 'onvif' | 'http' | 'https';
}

const initialState: RecorderFormState = {
  name: '',
  ipAddress: '',
  username: '',
  password: '',
  port: '80',
  httpPort: '80',
  rtspPort: '554',
  onvifPort: '8000',
  channelCount: '4',
  vendor: '',
  model: '',
  recorderType: 'dvr',
  protocol: 'rtsp',
};

export function RecorderForm({ onSubmit, onCancel }: DeviceFormProps) {
  const [form, setForm] = useState<RecorderFormState>(initialState);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = <Key extends keyof RecorderFormState>(key: Key, value: RecorderFormState[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const validate = () => {
    if (!form.name.trim()) { setError('Укажите название видеорегистратора.'); return false; }
    if (!form.ipAddress.trim()) { setError('Укажите IP-адрес или имя хоста.'); return false; }
    const ports = [form.port, form.httpPort, form.rtspPort, form.onvifPort];
    if (ports.some((port) => Number(port) < 1 || Number(port) > 65535)) { setError('Порты должны быть от 1 до 65535.'); return false; }
    if (Number(form.channelCount) < 0) { setError('Количество каналов не может быть отрицательным.'); return false; }
    return true;
  };

  const handleConnectionCheck = async () => {
    setError('');
    if (!validate()) return;
    setConnectionState('checking');
    setConnectionMessage('');
    try {
      // Backend check endpoint requires a saved device; for the pre-create test
      // we reuse the camera TCP/RTSP probe against the recorder's RTSP port.
      const result = await checkDeviceEndpoint({
        ipAddress: form.ipAddress.trim(),
        port: Number(form.rtspPort || 554),
      });
      setConnectionState(result.reachable ? 'success' : 'error');
      setConnectionMessage(
        result.reachable
          ? `Регистратор ответил за ${Math.max(1, Math.round(result.durationMs))} мс.`
          : result.message || 'Регистратор не ответил на проверку RTSP-порта.',
      );
    } catch (checkError) {
      setConnectionState('error');
      setConnectionMessage(checkError instanceof Error ? checkError.message : 'Не удалось выполнить проверку.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        deviceType: 'Dvr',
        name: form.name.trim(),
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        channelCount: Number(form.channelCount),
        vendor: [form.vendor.trim(), form.model.trim(), form.recorderType.toUpperCase()].filter(Boolean).join(' ').trim() || undefined,
      });
      onCancel();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось добавить видеорегистратор.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="camera-form" onSubmit={handleSubmit}>
      <div className="camera-form__scroller">
        <div className="camera-form__primary">
          <label className="field-group">
            <span>Название</span>
            <input required type="text" maxLength={200} value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="Например, Регистратор · Офис" />
          </label>
          <label className="field-group">
            <span>IP-адрес / хост</span>
            <input required type="text" inputMode="url" value={form.ipAddress} onChange={(event) => update('ipAddress', event.target.value)} placeholder="192.168.1.30" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </label>
          <label className="field-group">
            <span>Логин</span>
            <input type="text" value={form.username} onChange={(event) => update('username', event.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="admin" />
          </label>
          <PasswordField label="Пароль" value={form.password} onChange={(value) => update('password', value)} />
        </div>

        <button className="button button--secondary camera-form__check" type="button" onClick={() => void handleConnectionCheck()} disabled={connectionState === 'checking'}>
          {connectionState === 'checking' ? <LoaderCircle className="spin" size={18} /> : <CheckCircle2 size={18} />}
          {connectionState === 'checking' ? 'Проверяем…' : 'Проверить подключение'}
        </button>

        <ConnectionTest
          state={connectionState}
          successTitle="Регистратор доступен"
          successMessage={connectionMessage}
          errorTitle="Не удалось подключиться"
          errorMessage={connectionMessage}
        />

        <details className="camera-form__advanced" open>
          <summary><span>Параметры регистратора</span><ChevronDown size={18} aria-hidden="true" /></summary>
          <div className="camera-form__advanced-body">
            <div className="camera-form__grid">
              <label className="field-group">
                <span>Тип устройства</span>
                <select value={form.recorderType} onChange={(event) => update('recorderType', event.target.value as RecorderFormState['recorderType'])}>
                  <option value="dvr">DVR</option>
                  <option value="nvr">NVR</option>
                  <option value="xvr">XVR</option>
                </select>
              </label>
              <label className="field-group">
                <span>Количество каналов</span>
                <input required type="number" min="0" max="256" inputMode="numeric" value={form.channelCount} onChange={(event) => update('channelCount', event.target.value)} />
              </label>
              <label className="field-group">
                <span>Производитель</span>
                <input type="text" maxLength={100} value={form.vendor} onChange={(event) => update('vendor', event.target.value)} placeholder="Hikvision, Dahua…" />
              </label>
              <label className="field-group">
                <span>Модель</span>
                <input type="text" maxLength={100} value={form.model} onChange={(event) => update('model', event.target.value)} placeholder="DS-7204…" />
              </label>
            </div>
          </div>
        </details>

        <details className="camera-form__advanced">
          <summary><span>Порты и протоколы</span><ChevronDown size={18} aria-hidden="true" /></summary>
          <div className="camera-form__advanced-body">
            <div className="camera-form__grid">
              <label className="field-group">
                <span>Основной порт</span>
                <input required type="number" min="1" max="65535" inputMode="numeric" value={form.port} onChange={(event) => update('port', event.target.value)} />
              </label>
              <label className="field-group">
                <span>HTTP</span>
                <input type="number" min="1" max="65535" inputMode="numeric" value={form.httpPort} onChange={(event) => update('httpPort', event.target.value)} />
              </label>
              <label className="field-group">
                <span>RTSP</span>
                <input type="number" min="1" max="65535" inputMode="numeric" value={form.rtspPort} onChange={(event) => update('rtspPort', event.target.value)} />
              </label>
              <label className="field-group">
                <span>ONVIF</span>
                <input type="number" min="1" max="65535" inputMode="numeric" value={form.onvifPort} onChange={(event) => update('onvifPort', event.target.value)} />
              </label>
            </div>
            <label className="field-group">
              <span>Основной протокол подключения</span>
              <select value={form.protocol} onChange={(event) => update('protocol', event.target.value as RecorderFormState['protocol'])}>
                <option value="rtsp">RTSP</option>
                <option value="onvif">ONVIF</option>
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
              </select>
            </label>
          </div>
        </details>

        <p className="camera-form__security-note">Камеры назначаются на каналы регистратора из формы камеры или страницы каналов.</p>
        {error && <p className="camera-form__error" role="alert">{error}</p>}
      </div>
      <footer className="camera-dialog__actions">
        <button className="button button--secondary" type="button" onClick={onCancel}>Отмена</button>
        <button className="button button--primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Добавление…' : 'Добавить видеорегистратор'}</button>
      </footer>
    </form>
  );
}
